// React
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// i18n
import { Translate } from 'react-redux-i18n';

// Spectrum
import {
  Button,
  Divider,
  ProgressCircle,
  Text,
} from '@adobe/react-spectrum';

// Framer Motion
import { AnimatePresence, motion } from 'framer-motion';
import Trophy from '@spectrum-icons/workflow/Trophy';

// Preload action
import { getPartnerDetail } from '../../actions/partnerDetailAction';

// CSS
import '../../styles/ListingCard.scss';
import SocialMediaIcons from './SocialMediaIcons';
import { formatNumber } from '../../util/util';

const ListingCard = ({
  preloading,
  url,
  partnerName,
  shortDescription,
  address,
  partnerLevel,
  partnerLogoUrl,
  certifications,
  specializations,
  customerDeployments,
  partnerSolutions,
  assurancePrograms,
  badge,
  router,
  listingId,
  data,
  handleContactUsModal,
  isMobile,
}) => {
  const dispatch = useDispatch();

  /** handleCardClick
   * Handles clicking on a card.
   */
  const handleCardClick = (id, detailTab = null) => {
    dispatch(getPartnerDetail(id, true, router, detailTab));
  };

  /** handleCardKey
   * Handles key press on a card.
   */
  const handleCardKey = (e, id) => {
    if (e.key === 'Enter') {
      dispatch(getPartnerDetail(id, true, router));
    }
  };

  const handleContactUsButton = (id, name) => {
    handleContactUsModal(id, name);
  };

  /** renderPartnerLevel
   * Returns a translated partner level.
   */
  const renderPartnerLevel = () => {
    switch (partnerLevel) {
      case 'Bronze':
        return <Translate value="partner_finder.cards.bronze_partner" />;
      case 'Silver':
        return <Translate value="partner_finder.cards.silver_partner" />;
      case 'Gold':
        return <Translate value="partner_finder.cards.gold_partner" />;
      case 'Platinum':
        return <Translate value="partner_finder.cards.platinum_partner" />;
      case 'Platinum (Regional)':
        return <Translate value="partner_finder.cards.platinum_regional" />;
      case 'Community Plus':
        return <Translate value="partner_finder.cards.community_plus" />;
      case 'Community Free':
        return <Translate value="partner_finder.cards.community_free" />;
      default:
        return <Translate value="partner_finder.cards.partner" />;
    }
  };

  const getHQInfoText = () => {
    if (address) {
      if (address.city && address.country) {
        return `HQ: ${address.city}, ${address.country}`;
      }
      if (address.city) {
        return `HQ: ${address.city}`;
      }
      if (address.country) {
        return `HQ: ${address.country}`;
      }
    }
    return '';
  };

  return (
    <div data-testid="cmp-listing-card" className="cmp-listing-card">
      <div style={{ flexGrow: 1 }}>
        <div data-testid="listing-card-button" tabIndex={0} role="button" onClick={() => handleCardClick(url)} onKeyPress={(e) => handleCardKey(e, url)}>
          <AnimatePresence>
            {preloading ? (
              <motion.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ ease: 'easeInOut', duration: 0.5 }}
                className="preloading-overlay"
              >
                <ProgressCircle aria-label="Loading…" isIndeterminate />
              </motion.div>
            ) : null}
          </AnimatePresence>
          <div className="logo-container">
            <div className="logo" style={{ backgroundImage: `url(${partnerLogoUrl})` }} />
            {/* {badge && (
          <div className="award-info-container">
            <Trophy size="S" />
            <span className="award-info">
              {badge}
            </span>
          </div>
          )} */}
          </div>

          <div className="content-section">
            <Divider size="S" marginBottom={16} />
            <span className="partner-level">
              {renderPartnerLevel()}
            </span>
            <h1 className="partner-name">
              {partnerName}
            </h1>
            <span className="short-description">
              {shortDescription}
            </span>
            <span className="hqAddress">
              {getHQInfoText()}
            </span>

            {(specializations > 0
            || certifications > 0
            || customerDeployments > 0
            || partnerSolutions > 0
            || assurancePrograms > 0)
            && (
              <>
                <Divider size="S" marginTop={16} marginBottom={16} />
                <div className="card-certification-details">

                  {specializations > 0
                    ? (
                      <div className="certifications">
                        <Button onPress={() => handleCardClick(url, '#expertise#specializations')}>Specializations:</Button>
                        <span>{formatNumber(specializations)}</span>
                      </div>
                    ) : null}
                  {certifications > 0
                    ? (
                      <div className="certifications">
                        <Button onPress={() => handleCardClick(url, '#expertise#certifications')}>Certifications:</Button>
                        <span>{formatNumber(certifications)}</span>
                      </div>
                    ) : null}
                  {customerDeployments > 0
                    ? (
                      <div className="certifications">
                        <Button onPress={() => handleCardClick(url, '#expertise#customerDeployments')}>Customer Deployments:</Button>
                        <span>{formatNumber(customerDeployments)}</span>
                      </div>
                    ) : null}
                  {partnerSolutions > 0
                    ? (
                      <div className="certifications">
                        <Button onPress={() => handleCardClick(url, '#expertise#partnerSolutions')}>Partner Solutions:</Button>
                        <span>{formatNumber(partnerSolutions)}</span>
                      </div>
                    ) : null}
                  {assurancePrograms > 0
                    ? (
                      <div className="certifications">
                        <Button onPress={() => handleCardClick(url, '#expertise#partnerSolutions')}>App Assured:</Button>
                        <span>{formatNumber(assurancePrograms)}</span>
                      </div>
                    ) : null}
                </div>
              </>
            ) }
          </div>
        </div>
      </div>
      <div>
        <Divider size="S" marginTop={16} />
        <div className="listCardFooter">
          <SocialMediaIcons apiResponse={data} handleCardClick={() => handleCardClick(url)} />
          <Button
            UNSAFE_style={{
              cursor: 'pointer',
              border: '2px solid #000000E6',
              borderRadius: '20px',
            }}
            onPress={() => handleContactUsButton(listingId, partnerName)}
          >
            <Text UNSAFE_style={{ fontWeight: 'bold' }}>Contact Us</Text>
          </Button>
        </div>
      </div>
    </div>
  );
};

ListingCard.propTypes = {
  router: PropTypes.shape({
    navigate: PropTypes.func,
  }).isRequired,
  preloading: PropTypes.bool,
  url: PropTypes.string.isRequired,
  partnerName: PropTypes.string.isRequired,
  shortDescription: PropTypes.string,
  address: PropTypes.string,
  partnerLevel: PropTypes.string.isRequired,
  partnerLogoUrl: PropTypes.string,
  certifications: PropTypes.number,
  specializations: PropTypes.number,
  customerDeployments: PropTypes.number,
  assurancePrograms: PropTypes.number,
  partnerSolutions: PropTypes.number,
  badge: PropTypes.string,
  listingId: PropTypes.string,
  data: PropTypes.object,
};

ListingCard.defaultProps = {
  preloading: null,
  certifications: 0,
  specializations: 0,
  customerDeployments: 0,
  partnerSolutions: 0,
  assurancePrograms: 0,
  shortDescription: '',
  address: '',
  partnerLogoUrl: '',
  badge: '',
  listingId: '',
  data: {},
};

const mapStateToProps = (state) => ({
  isMobile: state.facets.isMobile,
});

const mapDispatchToProps = {
  getPartnerDetailAction: getPartnerDetail,
};

export default connect(mapStateToProps, mapDispatchToProps)(ListingCard);
